/**
 * Local candidate resolution — the "merge" half of extract-then-merge.
 *
 * Before this module, every extraction request embedded the FULL known-object
 * catalog (`knownObjectIds`, ~5,100 tokens per chunk at ~336 objects) so the
 * LLM could reuse canonical ids — a cost that grew with the graph, paid again
 * on every chunk. Now the request carries no catalog: the LLM names entities
 * naturally, and THIS resolver merges candidates onto existing objects locally.
 *
 * The auto-merge ladder is deliberately NARROW (a wrong auto-merge poisons the
 * graph; an unmerged candidate is just a pending proposal a human can still
 * merge later):
 *
 *   1. exact object id, then exact alias — the store's pre-existing
 *      `resolveAlias`, unchanged;
 *   2. typed slug equality — a `type:label-ish` ref whose normalized form
 *      `type:slugify(rest)` IS an existing object id;
 *   3. bare label — `slugify(ref)` matches exactly ONE object slug or alias
 *      slug across the whole graph; any ambiguity resolves to nothing.
 *
 * Anything fuzzier (edit distance, embeddings) is deliberately excluded here;
 * embedding similarity may later become a HINT surface, never an auto-merge
 * (per the 2026-06-29 review conditions).
 */

import type { OntosStore } from "../sqlite/store.js";
import type { Resolver } from "./validate.js";

/** Deterministic slug from a label (id = type:slug). Keeps unicode word chars. */
export function slugify(label: string): string {
  const s = label
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return s || "x";
}

export interface LocalResolver {
  /** Relation-endpoint resolver: ref (id | alias | natural label) → canonical id | null. */
  readonly resolveRef: Resolver;
  /**
   * Object-candidate merge probe: the existing object this candidate denotes,
   * or null when it is genuinely new (→ becomes an object proposal as before).
   */
  resolveObject(type: string, label: string, proposedId?: string | null): string | null;
}

/**
 * Build a resolver over a snapshot of the current graph. Snapshotting per
 * ingest run keeps lookups O(1) without touching the hot loop; the graph is
 * hundreds of objects, so the build cost is negligible.
 */
/**
 * Resolver-grade normalization: same shape as `slugify` but UNTRUNCATED (the
 * 60-char id cap could equate two long, distinct labels) and null for
 * content-free input (so nothing ever matches on slugify's `"x"` placeholder —
 * an emoji-only alias or punctuation-only candidate resolves to nothing).
 */
function normSlug(s: string | null | undefined): string | null {
  const t = (s ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return t || null;
}

export function createLocalResolver(store: OntosStore): LocalResolver {
  // Both maps hold ID SETS: a slug that could mean more than one object is
  // AMBIGUOUS and resolves to nothing — first-wins would silently pick a
  // wrong-merge winner (Codex round 1, finding 1).
  const typed = new Map<string, Set<string>>(); // `${type}:${slug}` → object ids
  const bare = new Map<string, Set<string>>(); // slug → canonical ids it could mean
  const put = (map: Map<string, Set<string>>, key: string, id: string): void => {
    const set = map.get(key);
    if (set) set.add(id);
    else map.set(key, new Set([id]));
  };
  const unique = (set: Set<string> | undefined): string | null =>
    set && set.size === 1 ? set.values().next().value ?? null : null;

  for (const obj of store.allObjectsLite()) {
    const colon = obj.id.indexOf(":");
    if (colon <= 0) continue;
    const type = obj.id.slice(0, colon);
    // Index BOTH spellings of the object: its id's slug part AND its display
    // label (they differ — e.g. a romanized id with a native-script label).
    // The id's slug part is stored 60-char-truncated; indexing it verbatim is
    // fine because CANDIDATE slugs are never truncated — a >60-char source can
    // only merge via its (untruncated) label, never via a truncation collision.
    const slugs = new Set([normSlug(obj.id.slice(colon + 1)), normSlug(obj.label)]);
    for (const slug of slugs) {
      if (!slug) continue;
      put(typed, `${type}:${slug}`, obj.id);
      put(bare, slug, obj.id);
    }
  }
  for (const { alias, canonicalId } of store.allAliases()) {
    const slug = normSlug(alias);
    if (slug) put(bare, slug, canonicalId);
  }

  const resolveRef: Resolver = (ref) => {
    if (!ref || typeof ref !== "string") return null;
    // Exact id/alias equality is trusted VERBATIM — aliases are human- or
    // deterministically seeded, so even a punctuation-only alias resolves
    // here by design; the content-free guard below applies to SLUG matching
    // only (Codex round 2: documented as intentional).
    const exact = store.resolveAlias(ref);
    if (exact) return exact;
    const colon = ref.indexOf(":");
    if (colon > 0 && /^[a-z]+$/.test(ref.slice(0, colon))) {
      // Typed ref: normalize only the slug part; never fall through to a bare
      // match (that could silently merge across types).
      const slug = normSlug(ref.slice(colon + 1));
      return slug ? unique(typed.get(`${ref.slice(0, colon)}:${slug}`)) : null;
    }
    const slug = normSlug(ref);
    return slug ? unique(bare.get(slug)) : null;
  };

  const resolveObject = (type: string, label: string, proposedId?: string | null): string | null => {
    const labelSlug = normSlug(label);
    const viaLabel = labelSlug ? unique(typed.get(`${type}:${labelSlug}`)) : null;
    const viaId = proposedId ? resolveRef(proposedId) : null;
    // Two signals pointing at DIFFERENT objects = a conflict, not a merge
    // (Codex round 1, finding 4).
    if (viaLabel && viaId && viaLabel !== viaId) return null;
    // A contentful label that does NOT corroborate blocks an id-only merge: a
    // stale or truncation-colliding proposedId alone must not absorb a
    // candidate whose actual label names something else. The unmerged
    // candidate just stays a pending proposal (Codex round 2, finding 1).
    if (viaId && labelSlug && !viaLabel) return null;
    return viaLabel ?? viaId;
  };

  return { resolveRef, resolveObject };
}
